import LocalizedStrings from 'localized-strings';

import { ScheduledBankTransferFrequency } from '__generated__/GQL';

interface CreateNewTransferSuccessLocalizedType {
  external: {
    instant: {
      description: string;
      title: string;
      details: string;
    };
    scheduled: {
      description: string;
      title: string;
    };
    standard: {
      description: string;
      title: string;
      details: string;
    };
  };
  scheduledTransferDetails: {
    recurrentWithEndDate: {
      [ScheduledBankTransferFrequency.EveryMonth]: string;
      [ScheduledBankTransferFrequency.EveryWeek]: string;
      [ScheduledBankTransferFrequency.EveryYear]: string;
    };
    recurrentWithoutEndDate: {
      [ScheduledBankTransferFrequency.Once]: string;
      [ScheduledBankTransferFrequency.EveryMonth]: string;
      [ScheduledBankTransferFrequency.EveryWeek]: string;
      [ScheduledBankTransferFrequency.EveryYear]: string;
    };
  };
}

const locales = new LocalizedStrings<CreateNewTransferSuccessLocalizedType>({
  en: {
    external: {
      instant: {
        description:
          "You've just sent **{amount}** from **{from}** to **{to}**.",
        details: 'The transfer will be received within seconds.',
        title: 'Instant transfer completed!',
      },
      scheduled: {
        description:
          "You've just scheduled a transfer of **{amount}** from **{from}** to **{to}**.",
        title: 'Transfer scheduled successfully!',
      },
      standard: {
        description:
          "You've just sent **{amount}** from **{from}** to **{to}**.",
        details: 'The transfer will be received within 2-3 business days.',
        title: 'Transfer registered!',
      },
    },
    scheduledTransferDetails: {
      recurrentWithEndDate: {
        [ScheduledBankTransferFrequency.EveryMonth]:
          'It will be done for the first time on {startDate} and will be renewed every month until {endDate}.',
        [ScheduledBankTransferFrequency.EveryWeek]:
          'It will be done for the first time on {startDate} and will be renewed every week until {endDate}.',
        [ScheduledBankTransferFrequency.EveryYear]:
          'It will be done for the first time on {startDate} and will be renewed every year until {endDate}.',
      },
      recurrentWithoutEndDate: {
        [ScheduledBankTransferFrequency.Once]: 'It will be done on {date}.',
        [ScheduledBankTransferFrequency.EveryMonth]:
          'It will be done for the first time on {startDate} and will be renewed every month.',
        [ScheduledBankTransferFrequency.EveryWeek]:
          'It will be done for the first time on {startDate} and will be renewed every week.',
        [ScheduledBankTransferFrequency.EveryYear]:
          'It will be done for the first time on {startDate} and will be renewed every year.',
      },
    },
  },

  fr: {
    external: {
      instant: {
        description:
          'Vous avez envoyé **{amount}** depuis **{from}** à **{to}**.',
        details: 'Le virement sera reçu d’ici quelques secondes.',
        title: 'Virement instantané effectué !',
      },
      scheduled: {
        description:
          'Vous venez de programmer un virement de **{amount}** depuis **{from}** à **{to}**. ',
        title: 'Virement programmé avec succès !',
      },
      standard: {
        description:
          'Vous avez envoyé **{amount}** depuis **{from}** à **{to}**.',
        details: 'Le virement sera reçu d’ici 2 à 3 jours ouvrés.',
        title: 'Virement pris en compte !',
      },
    },
    scheduledTransferDetails: {
      recurrentWithEndDate: {
        [ScheduledBankTransferFrequency.EveryMonth]: `Il sera effectué une première fois le {date} et sera renouvelé tous les mois jusqu'au {endDate}.`,
        [ScheduledBankTransferFrequency.EveryWeek]: `Il sera effectué une première fois le {date} et sera renouvelé toutes les semaines jusqu'au {endDate}.`,
        [ScheduledBankTransferFrequency.EveryYear]: `Il sera effectué une première fois le {date} et sera renouvelé tous les ans jusqu'au {endDate}.`,
      },
      recurrentWithoutEndDate: {
        [ScheduledBankTransferFrequency.EveryMonth]:
          'Il sera effectué une première fois le {date} et sera renouvelé tous les mois',
        [ScheduledBankTransferFrequency.EveryWeek]:
          'Il sera effectué une première fois le {date} et sera renouvelé toutes les semaines',
        [ScheduledBankTransferFrequency.EveryYear]:
          'Il sera effectué une première fois le {date} et sera renouvelé tous les ans',
        [ScheduledBankTransferFrequency.Once]: 'Il sera effectué le {date}.',
      },
    },
  },
} as const);

export default locales;
